import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";

import socket from "../services/socket";
import { ApiServices } from "../utils/ApiService";

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [count, setCount] = useState(0);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const connectedUserRef = useRef(null);

  // ============================================================
  // GET CURRENT USER
  // ============================================================

  const getCurrentUser = useCallback(() => {
    try {
      const storedUser = localStorage.getItem("user");

      if (!storedUser) {
        return null;
      }

      const parsedUser = JSON.parse(storedUser);

      return parsedUser;
    } catch (error) {
      console.error("Unable to parse stored user:", error);
      return null;
    }
  }, []);

  // ============================================================
  // FETCH ALL NOTIFICATIONS
  // ============================================================

  const fetchNotifications = useCallback(
    async () => {
      try {
        setLoading(true);

        /*
         * IMPORTANT:
         *
         * Do NOT send userId here.
         *
         * Backend gets userId from:
         *
         * req.user.id
         *
         * through authentication.
         */

        const response = await ApiServices.get(`/notifications`);

        const result = response?.data;

        const newData = result?.data || [];

        // if (pageNumber === 1) {
        if(newData?.length > 0) setNotifications(newData);
        // } else {
        //   setNotifications((previous) => {
        //     const existingIds = new Set(
        //       previous.map((item) => item.id)
        //     );

        //     const filteredData = newData.filter(
        //       (item) => !existingIds.has(item.id)
        //     );

        //     return [
        //       ...previous,
        //       ...filteredData,
        //     ];
        //   });
        // }

        // setPage(pageNumber);

        // setHasMore(
        //   pageNumber <
        //     (result?.totalPages || 0)
        // );
      } catch (error) {
        console.error(
          "Notification fetch error:",
          error
        );
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // ============================================================
  // FETCH UNREAD COUNT
  // ============================================================

  const fetchUnreadCount = useCallback(async () => {
    try {
      console.log(
        "Fetching unread notification count..."
      );

      const response = await ApiServices.get(
        "/notifications/unread-count"
      );

      setCount(
        response?.data?.count || 0
      );
    } catch (error) {
      console.error(
        "Unread count error:",
        error
      );
    }
  }, []);

  // ============================================================
  // CONNECT SOCKET
  // ============================================================

  const connectSocket = useCallback(
    (userId) => {
      if (!userId) {
        console.log(
          "No user ID. Notification socket not started."
        );
        return;
      }

      console.log(
        "Initializing notification system for user:",
        userId
      );

      // Prevent duplicate setup
      if (
        connectedUserRef.current === userId &&
        socket.connected
      ) {
        console.log(
          "Notification socket already connected."
        );

        // Still fetch notifications
        fetchNotifications();
        fetchUnreadCount();

        return;
      }

      connectedUserRef.current = userId;

      // Remove previous listeners
      socket.off("notification");
      socket.off("connect");

      // ========================================================
      // SOCKET CONNECT
      // ========================================================

      const joinRoom = () => {
        console.log(
          "Notification socket connected:",
          socket.id
        );

        console.log(
          "Joining notification room:",
          userId
        );

        socket.emit("join", userId);
      };

      socket.on(
        "connect",
        joinRoom
      );

      // ========================================================
      // REAL-TIME NOTIFICATION
      // ========================================================

      socket.on(
        "notification",
        (notification) => {

          setNotifications((previous) => {
            const exists = previous.some(
              (item) =>
                item.id === notification.id
            );

            if (exists) {
              return previous;
            }

            return [
              {
                ...notification,
                isRead: false,
                readAt: null,
              },
              ...previous,
            ];
          });

          setCount(
            (previous) =>
              previous + 1
          );
        }
      );

      // ========================================================
      // CONNECT SOCKET
      // ========================================================

      if (!socket.connected) {
        console.log(
          "Connecting notification socket..."
        );

        socket.connect();
      } else {
        console.log(
          "Socket already connected. Joining room..."
        );

        joinRoom();
      }

      // ========================================================
      // LOAD EXISTING NOTIFICATIONS
      // ========================================================

      fetchNotifications();
      fetchUnreadCount();
    },
    [
      fetchNotifications,
      fetchUnreadCount,
    ]
  );

  // ============================================================
  // AUTOMATIC INITIALIZATION
  //
  // THIS IS THE IMPORTANT PART
  // ============================================================

  useEffect(() => {
    console.log(
      "NotificationProvider mounted"
    );

    const storedUser =
      localStorage.getItem("user");

    console.log(
      "Stored user:",
      storedUser
    );

    if (!storedUser) {
      console.log(
        "No user found in localStorage."
      );

      return;
    }

    try {
      const user =
        JSON.parse(storedUser);

      console.log(
        "Logged-in user:",
        user
      );

      if (user?.id) {
        connectSocket(user.id);
      } else {
        console.log(
          "User ID not found."
        );
      }
    } catch (error) {
      console.error(
        "Invalid user in localStorage:",
        error
      );
    }

    // ========================================================
    // CLEANUP
    // ========================================================

    return () => {
      console.log(
        "NotificationProvider cleanup"
      );

      socket.off(
        "notification"
      );

      socket.off("connect");

      connectedUserRef.current = null;
    };
  }, [connectSocket]);

  // ============================================================
  // DISCONNECT SOCKET
  // ============================================================

  const disconnectSocket =
    useCallback(() => {
      console.log(
        "Disconnecting notification socket..."
      );

      socket.off(
        "notification"
      );

      socket.off("connect");

      if (socket.connected) {
        socket.disconnect();
      }

      connectedUserRef.current = null;

      setNotifications([]);
      setCount(0);
      setPage(1);
      setHasMore(true);
    }, []);

  // ============================================================
  // MARK ONE AS READ
  // ============================================================

  const markAsRead =
    useCallback(async (id) => {
      try {
        const notification =
          notifications.find(
            (item) =>
              item.id === id
          );

        if (!notification) {
          return;
        }

        if (notification.isRead) {
          return;
        }

        console.log(
          "Marking notification as read:",
          id
        );

        await ApiServices.put(
          `/notifications/read/${id}`
        );

        setNotifications(
          (previous) =>
            previous.map(
              (item) =>
                item.id === id
                  ? {
                      ...item,
                      isRead: true,
                      readAt:
                        new Date().toISOString(),
                    }
                  : item
            )
        );

        setCount(
          (previous) =>
            Math.max(
              previous - 1,
              0
            )
        );
      } catch (error) {
        console.error(
          "Mark as read error:",
          error
        );
      }
    }, [notifications]);

  // ============================================================
  // MARK ALL AS READ
  // ============================================================

  const markAllAsRead =
    useCallback(async () => {
      try {
        await ApiServices.put(
          "/notifications/read-all"
        );

        setNotifications(
          (previous) =>
            previous.map(
              (item) => ({
                ...item,
                isRead: true,
                readAt:
                  item.readAt ||
                  new Date().toISOString(),
              })
            )
        );

        setCount(0);
      } catch (error) {
        console.error(
          "Mark all as read error:",
          error
        );
      }
    }, []);

  // ============================================================
  // LOAD MORE
  // ============================================================

  // const loadMore =
  //   useCallback(async () => {
  //     if (loading || !hasMore) {
  //       return;
  //     }

  //     await fetchNotifications(
  //       page + 1
  //     );
  //   }, [
  //     loading,
  //     hasMore,
  //     page,
  //     fetchNotifications,
  //   ]);

  // ============================================================
  // RETURN PROVIDER
  // ============================================================

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        count,

        loading,
        hasMore,
        page,

        connectSocket,
        disconnectSocket,

        fetchNotifications,
        fetchUnreadCount,
        // loadMore,

        markAsRead,
        markAllAsRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

// ============================================================
// HOOK
// ============================================================

export const useNotification = () =>
  useContext(NotificationContext);